import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LoginPage } from './pages/LoginPage';
import { CategoryList } from './pages/CategoryList';
import { ProductListPage } from './pages/ProductListPage';
import { PhoneSimulator } from './pages/PhoneSimulator';
import { Footer } from './components/Footer';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

/** Redirect unauthenticated users to /login */
function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected */}
      <Route path="/" element={<Navigate to="/categories" replace />} />
      <Route path="/categories" element={<RequireAuth><CategoryList /></RequireAuth>} />
      <Route path="/categories/:categoryId/products" element={<RequireAuth><ProductListPage /></RequireAuth>} />
      <Route path="/simulator" element={<RequireAuth><PhoneSimulator /></RequireAuth>} />

      {/* Catch-all — redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <>
              <AppRoutes />
              <Footer />
            </>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
